import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditDialog } from '../../../EditDialog';
import { EditFormField } from '../../../EditFormField';
import { EditTextareaField } from '../../../EditTextareaField';
import type { ContactAddressBusiness } from '../queries/contactAddressesQuery';
import { GET_CONTACT_ADDRESSES } from '../queries/contactAddressesQuery';

const UPSERT_CONTACT_PRIMARY_ADDRESS = gql`
  mutation UpsertContactPrimaryAddress(
    $contactId: ID!
    $values: ContactPrimaryAddressUpsertInput!
  ) {
    upsertContactPrimaryAddress(contactId: $contactId, values: $values) {
      ok
      created
      contactPrimaryAddress {
        id
        businessCity
        businessAddress
        businessStreet
        businessStreet2
        address1Street3
        businessState
        businessPostalCode
        businessCountryRegion
        businessPhone
        businessPhone2
        businessFax
        businessFax2
        address1County
        address1PostOfficeBox
        address1Latitude
        address1Longitude
        address1Telephone1
      }
    }
  }
`;

export interface EditBusinessAddressDialogProps {
  contactId: string;
  initialValue: ContactAddressBusiness;
  isOpen: boolean;
  onChange: (data: ContactAddressBusiness) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactAddressBusiness;
}

export function EditBusinessAddressDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditBusinessAddressDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [upsertAddress] = useMutation(UPSERT_CONTACT_PRIMARY_ADDRESS, {
    refetchQueries: [
      { query: GET_CONTACT_ADDRESSES, variables: { id: contactId } }
    ]
  });

  const changes = getChangedValues(value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await upsertAddress({
        variables: { contactId, values: changes }
      });

      const data = response.data as {
        upsertContactPrimaryAddress?: { ok?: boolean };
      };

      if (data.upsertContactPrimaryAddress?.ok === false) {
        toast.error('Failed to save business address');
      } else {
        toast.success('Business address saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving business address:', error);
      toast.error('Failed to save business address');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit Business Address"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditFormField
          label="Street 1"
          onChange={street1 => {
            onChange({ ...value, street1 });
          }}
          value={value.street1}
        />
        <EditFormField
          label="Street 2"
          onChange={street2 => {
            onChange({ ...value, street2 });
          }}
          value={value.street2}
        />
        <EditFormField
          label="Street 3"
          onChange={street3 => {
            onChange({ ...value, street3 });
          }}
          value={value.street3}
        />
        <EditFormField
          label="City"
          onChange={city => {
            onChange({ ...value, city });
          }}
          value={value.city}
        />
        <EditFormField
          label="State/Province"
          onChange={stateProvince => {
            onChange({ ...value, stateProvince });
          }}
          value={value.stateProvince}
        />
        <EditFormField
          label="ZIP/Postal Code"
          onChange={postalCode => {
            onChange({ ...value, postalCode });
          }}
          value={value.postalCode}
        />
        <EditFormField
          label="Country/Region"
          onChange={countryRegion => {
            onChange({ ...value, countryRegion });
          }}
          value={value.countryRegion}
        />
        <EditFormField
          label="County"
          onChange={county => {
            onChange({ ...value, county });
          }}
          value={value.county}
        />
        <EditFormField
          label="Post Office Box"
          onChange={postOfficeBox => {
            onChange({ ...value, postOfficeBox });
          }}
          value={value.postOfficeBox}
        />
        <EditFormField
          label="Latitude"
          onChange={val => {
            const num = parseFloat(val);
            onChange({ ...value, latitude: isNaN(num) ? null : num });
          }}
          type="number"
          value={value.latitude?.toString() ?? ''}
        />
        <EditFormField
          label="Longitude"
          onChange={val => {
            const num = parseFloat(val);
            onChange({ ...value, longitude: isNaN(num) ? null : num });
          }}
          type="number"
          value={value.longitude?.toString() ?? ''}
        />
        <EditFormField
          label="Phone"
          onChange={phone => {
            onChange({ ...value, phone });
          }}
          type="tel"
          value={value.phone}
        />
        <EditFormField
          label="Phone 2"
          onChange={phone2 => {
            onChange({ ...value, phone2 });
          }}
          type="tel"
          value={value.phone2}
        />
        <EditFormField
          label="Address 1 Telephone 1"
          onChange={address1Telephone1 => {
            onChange({ ...value, address1Telephone1 });
          }}
          type="tel"
          value={value.address1Telephone1}
        />
        <EditFormField
          label="Fax"
          onChange={fax => {
            onChange({ ...value, fax });
          }}
          value={value.fax}
        />
        <EditFormField
          label="Fax 2"
          onChange={fax2 => {
            onChange({ ...value, fax2 });
          }}
          value={value.fax2}
        />
        <div className="col-span-2 mt-4">
          <EditTextareaField
            label="Full Address"
            onChange={fullAddress => {
              onChange({ ...value, fullAddress });
            }}
            value={value.fullAddress}
          />
        </div>
      </div>
    </EditDialog>
  );
}

function getChangedValues(
  current: ContactAddressBusiness,
  initial: ContactAddressBusiness
): Record<string, null | number | string> {
  const changes: Record<string, null | number | string> = {};

  if (current.street1 !== initial.street1)
    changes.businessStreet = current.street1 || null;
  if (current.street2 !== initial.street2)
    changes.businessStreet2 = current.street2 || null;
  if (current.street3 !== initial.street3)
    changes.address1Street3 = current.street3 || null;
  if (current.city !== initial.city)
    changes.businessCity = current.city || null;
  if (current.stateProvince !== initial.stateProvince)
    changes.businessState = current.stateProvince || null;
  if (current.postalCode !== initial.postalCode)
    changes.businessPostalCode = current.postalCode || null;
  if (current.countryRegion !== initial.countryRegion)
    changes.businessCountryRegion = current.countryRegion || null;
  if (current.phone !== initial.phone)
    changes.businessPhone = current.phone || null;
  if (current.phone2 !== initial.phone2)
    changes.businessPhone2 = current.phone2 || null;
  if (current.fax !== initial.fax) changes.businessFax = current.fax || null;
  if (current.fax2 !== initial.fax2)
    changes.businessFax2 = current.fax2 || null;
  if (current.fullAddress !== initial.fullAddress)
    changes.businessAddress = current.fullAddress || null;
  if (current.county !== initial.county)
    changes.address1County = current.county || null;
  if (current.postOfficeBox !== initial.postOfficeBox)
    changes.address1PostOfficeBox = current.postOfficeBox || null;
  if (current.latitude !== initial.latitude)
    changes.address1Latitude = current.latitude;
  if (current.longitude !== initial.longitude)
    changes.address1Longitude = current.longitude;
  if (current.address1Telephone1 !== initial.address1Telephone1)
    changes.address1Telephone1 = current.address1Telephone1 || null;

  return changes;
}
